const API_BASE = import.meta.env.VITE_API_URL || '/api';

class ApiClient {
  private token: string | null = null;

  constructor() {
    this.token = localStorage.getItem('token');
  }

  setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  }

  getToken() {
    return this.token;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
    });

    if (response.status === 401) {
      this.setToken(null);
      window.location.href = '/login';
      throw new Error('Unauthorized');
    }

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Request failed');
    }

    return data;
  }

  // Auth
  async login(email: string, password: string) {
    const data = await this.request<{ token: string; user: any }>('/admin/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    this.setToken(data.token);
    return data;
  }

  async getMe() {
    return this.request<{ id: string; email: string; role: string; timezone: string }>('/admin/auth/me');
  }

  async updateProfile(data: { timezone?: string }) {
    return this.request<{ id: string; email: string; role: string; timezone: string }>('/admin/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  logout() {
    this.setToken(null);
  }

  // Users
  async getUsers() {
    return this.request<{ users: any[] }>('/admin/users');
  }

  async createUser(data: { email: string; password: string; role: string }) {
    return this.request<any>('/admin/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUser(id: string, data: any) {
    return this.request<any>(`/admin/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteUser(id: string) {
    return this.request<any>(`/admin/users/${id}`, {
      method: 'DELETE',
    });
  }

  // Projects
  async getProjects(includeHidden?: boolean) {
    const query = includeHidden ? '?includeHidden=1' : '';
    return this.request<{ projects: any[] }>(`/admin/projects${query}`);
  }

  async getProject(id: string) {
    return this.request<any>(`/admin/projects/${id}`);
  }

  async createProject(data: { name: string; description?: string }) {
    return this.request<any>('/admin/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateProject(id: string, data: any) {
    return this.request<any>(`/admin/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteProject(id: string) {
    return this.request<any>(`/admin/projects/${id}`, {
      method: 'DELETE',
    });
  }

  async reorderProjects(items: Array<{ id: string; sort_order: number }>) {
    return this.request<{ success: boolean }>('/admin/projects/reorder', {
      method: 'PUT',
      body: JSON.stringify({ items }),
    });
  }

  // API Keys
  async getApiKeys(projectId?: string) {
    const query = projectId ? `?projectId=${projectId}` : '';
    return this.request<{ apiKeys: any[] }>(`/admin/api-keys${query}`);
  }

  async createApiKey(data: {
    projectId: string;
    name: string;
    permissions?: string[];
    rateLimit?: number;
    dailyLimit?: number;
  }) {
    return this.request<any>('/admin/api-keys', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateApiKey(id: string, data: any) {
    return this.request<any>(`/admin/api-keys/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteApiKey(id: string) {
    return this.request<any>(`/admin/api-keys/${id}`, {
      method: 'DELETE',
    });
  }

  async regenerateApiKey(id: string) {
    return this.request<any>(`/admin/api-keys/${id}/regenerate`, {
      method: 'POST',
    });
  }

  // Stats
  async getOverviewStats(project?: string) {
    const query = project ? `?project=${project}` : '';
    return this.request<any>(`/admin/stats/overview${query}`);
  }

  async getProjectStats(id: string) {
    return this.request<any>(`/admin/stats/projects/${id}`);
  }

  async getDailyStats(from: string, to: string, projectId?: string) {
    let query = `?from=${from}&to=${to}`;
    if (projectId) query += `&projectId=${projectId}`;
    return this.request<{ stats: any[] }>(`/admin/stats/daily${query}`);
  }

  async getEmails(filters: {
    status?: string;
    domain?: string;
    page?: number;
    limit?: number;
  }) {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.domain) params.set('domain', filters.domain);
    if (filters.page) params.set('page', filters.page.toString());
    if (filters.limit) params.set('limit', filters.limit.toString());
    return this.request<any>(`/admin/stats/emails?${params}`);
  }

  async getAllEmails(params: { page?: number; limit?: number; search?: string; source?: string } = {}) {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', String(params.page));
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.search) qs.set('search', params.search);
    if (params.source) qs.set('source', params.source);
    return this.request<{
      emails: Array<{
        email: string;
        status: string;
        ub_status: string | null;
        last_activity: string;
        has_usebouncer: boolean;
        has_brevo: boolean;
        ub_id: string | null;
        brevo_delivered: number;
        brevo_opened: number;
        brevo_hard_bounced: number;
        brevo_soft_bounced: number;
        brevo_sent: number;
        projects: string[];
      }>;
      total: number;
      page: number;
      pages: number;
    }>(`/admin/stats/all-emails?${qs}`);
  }

  // Email Explorer
  async getEmailsExplorer(filters: Record<string, any> = {}) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== '' && value !== 'all') {
        params.set(key, String(value));
      }
    }
    return this.request<{
      emails: any[];
      total: number;
      page: number;
      pages: number;
    }>(`/admin/stats/emails-explorer?${params}`);
  }

  async getEmailsExplorerMeta() {
    return this.request<{
      reasons: string[];
      providers: string[];
      sources: string[];
      domains_top: Array<{ domain: string; count: number }>;
      projects: Array<{ id: string; name: string }>;
    }>('/admin/stats/emails-explorer/meta');
  }

  async startEmailsExport(filters: Record<string, any> = {}): Promise<{ jobId: string; rowsTotal: number }> {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== '' && value !== 'all' && key !== 'page') {
        params.set(key, String(value));
      }
    }
    return this.request<{ jobId: string; rowsTotal: number }>(
      `/admin/stats/emails-explorer/export?${params}`,
      { method: 'POST' }
    );
  }

  async listEmailsExports() {
    return this.request<{
      jobs: Array<{
        jobId: string;
        status: 'pending' | 'processing' | 'done' | 'failed';
        filters: Record<string, any>;
        rowsDone: number;
        rowsTotal: number | null;
        downloadUrl: string | null;
        error: string | null;
        createdAt: string;
        finishedAt: string | null;
      }>;
    }>(`/admin/stats/emails-explorer/exports`);
  }

  async deleteEmailsExport(jobId: string) {
    return this.request<{ ok: true }>(`/admin/stats/emails-explorer/export/${jobId}`, {
      method: 'DELETE',
    });
  }

  async getEmailsExportStatus(jobId: string) {
    return this.request<{
      jobId: string;
      status: 'pending' | 'processing' | 'done' | 'failed';
      rowsDone: number;
      rowsTotal: number | null;
      downloadUrl: string | null;
      error: string | null;
    }>(`/admin/stats/emails-explorer/export/${jobId}`);
  }

  async downloadEmailsExport(jobId: string): Promise<Blob> {
    const headers: Record<string, string> = {};
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
    const response = await fetch(
      `${API_BASE}/admin/stats/emails-explorer/export/${jobId}/download`,
      { headers }
    );
    if (response.status === 401) {
      this.setToken(null);
      window.location.href = '/login';
      throw new Error('Unauthorized');
    }
    if (!response.ok) throw new Error('Download failed');
    return await response.blob();
  }

  async getTopDomains() {
    return this.request<{ domains: any[] }>('/admin/stats/top-domains');
  }

  async getRecentVerifications(limit = 20) {
    return this.request<{ verifications: any[] }>(`/admin/stats/recent?limit=${limit}`);
  }

  async getEmailDetails(emailId: string) {
    return this.request<{ email: any; history: any[]; brevoEvents?: any[] }>(`/admin/stats/emails/${emailId}`);
  }

  async searchEmails(query: string, limit = 10) {
    const params = new URLSearchParams({ q: query, limit: String(limit) });
    return this.request<{ emails: any[]; total: number }>(`/admin/stats/emails/search?${params}`);
  }

  async getEmailByAddress(email: string) {
    return this.request<{
      email: string;
      has_usebouncer: boolean;
      has_brevo: boolean;
      details: any | null;
      history: any[];
      projects: Array<{ id: string; name: string }>;
      brevoEvents: any[];
      brevoSummary: { sent: number; delivered: number; opened: number; clicked: number; bounced: number; unsubscribed: number; complained: number; blocked: number; deferred: number; lastEventAt: string | null };
    }>(`/admin/stats/emails/by-address/${encodeURIComponent(email)}`);
  }

  // Providers
  async getProviders() {
    return this.request<{ providers: any[] }>('/admin/providers');
  }

  async getCredits() {
    return this.request<{ credits: number | null; error?: string }>('/admin/providers/credits');
  }

  async getProvider(id: string) {
    return this.request<any>(`/admin/providers/${id}`);
  }

  async createProvider(data: {
    name: string;
    type: string;
    apiKey?: string;
    apiUrl?: string;
    isDefault?: boolean;
    settings?: Record<string, any>;
  }) {
    return this.request<any>('/admin/providers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateProvider(id: string, data: any) {
    return this.request<any>(`/admin/providers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteProvider(id: string) {
    return this.request<any>(`/admin/providers/${id}`, {
      method: 'DELETE',
    });
  }

  async testProvider(id: string) {
    return this.request<{ success: boolean; message?: string; credits?: number; email?: string; companyName?: string; error?: string }>(
      `/admin/providers/${id}/test`,
      { method: 'POST' }
    );
  }

  // Manual Verification
  async verifySingle(email: string, projectId: string) {
    return this.request<any>('/admin/verify/single', {
      method: 'POST',
      body: JSON.stringify({ email, projectId }),
    });
  }

  async verifyBatch(emails: string[], projectId: string) {
    return this.request<any>('/admin/verify/batch', {
      method: 'POST',
      body: JSON.stringify({ emails, projectId }),
    });
  }

  async createBatchJob(emails: string[], projectId: string) {
    return this.request<{ batchId: string; total: number; status: string }>('/admin/verify/batch/create', {
      method: 'POST',
      body: JSON.stringify({ emails, projectId }),
    });
  }

  async processBatchChunk(batchId: string) {
    return this.request<{
      status: string;
      processed: number;
      total: number;
      cacheHits: number;
      results?: any[];
    }>(`/admin/verify/batch/${batchId}/process`, {
      method: 'POST',
    });
  }

  async stopBatch(batchId: string) {
    return this.request<{ success: boolean; status: string }>(`/admin/verify/batch/${batchId}/stop`, {
      method: 'POST',
    });
  }

  async getActiveBatches() {
    return this.request<{
      batches: {
        id: string;
        projectName: string;
        status: string;
        totalCount: number;
        processedCount: number;
        cacheHits: number;
        createdAt: string;
        progress: number;
      }[];
    }>('/admin/verify/batch/active');
  }

  async getBatches(projectId?: string) {
    const query = projectId ? `?projectId=${projectId}` : '';
    return this.request<{ batches: any[] }>(`/admin/verify/batches${query}`);
  }

  async getBatchStatus(id: string) {
    return this.request<any>(`/admin/verify/batch/${id}`);
  }

  async getBatchResults(id: string) {
    return this.request<{ emails: any[] }>(`/admin/verify/batch/${id}/results`);
  }
  // API Logs
  async getApiLogs(filters: {
    operation?: string;
    isError?: boolean;
    page?: number;
    limit?: number;
  } = {}) {
    const params = new URLSearchParams();
    if (filters.operation) params.set('operation', filters.operation);
    if (filters.isError) params.set('isError', '1');
    if (filters.page) params.set('page', filters.page.toString());
    if (filters.limit) params.set('limit', filters.limit.toString());
    return this.request<{
      logs: any[];
      pagination: { page: number; limit: number; total: number; totalPages: number };
    }>(`/admin/api-logs?${params}`);
  }

  async getApiLogDetail(id: string) {
    return this.request<{ log: any }>(`/admin/api-logs/${id}`);
  }

  async cleanupApiLogs(days = 30) {
    return this.request<{ deleted: number; message: string }>(`/admin/api-logs/cleanup?days=${days}`, {
      method: 'DELETE',
    });
  }

  // Brevo
  async getBrevoEvents(email: string) {
    return this.request<{
      email: string;
      effectiveStatus: string;
      useBouncerStatus: string;
      summary: { sent: number; delivered: number; opened: number; clicked: number; bounced: number; lastEventAt: string | null };
      events: any[];
    }>(`/admin/brevo/events/${encodeURIComponent(email)}`);
  }

  async startBrevoSync() {
    return this.request<{ synced: number; skipped: number; hasMore: boolean }>('/admin/brevo/sync', {
      method: 'POST',
    });
  }

  // System
  async checkPostgres() {
    return this.request<{
      configured: boolean;
      connected: boolean;
      version?: string;
      writeAccess?: boolean;
      writeError?: string;
      schemaComplete?: boolean;
      missingTables?: string[];
      tables?: Array<{ name: string; rows: number; sizeBytes: number }>;
      counts?: Record<string, number>;
      message: string;
    }>('/admin/system/check-postgres', { method: 'POST' });
  }

  async checkClickHouse() {
    return this.request<{
      configured: boolean;
      connected: boolean;
      writeAccess?: boolean;
      writeError?: string;
      schemaComplete?: boolean;
      missingTables?: string[];
      tables?: Array<{ name: string; engine: string; rows: number; sizeBytes: number }>;
      counts?: Record<string, number>;
      message: string;
    }>('/admin/system/check-clickhouse', { method: 'POST' });
  }

  async getBrevoSyncStatus() {
    return this.request<{
      providers: Array<{
        providerId: string;
        providerName: string;
        lastEventDate: string | null;
        lastOffset: number;
        lastSyncedAt: string | null;
        totalEvents: number;
        status: string;
      }>;
      summary: {
        totalProviders: number;
        totalEvents: number;
        lastSyncedAt: string | null;
        status: string;
      };
    }>('/admin/brevo/sync/status');
  }

  // Brevo Webhooks
  async getBrevoWebhooks() {
    return this.request<{
      webhooks: Array<{
        id: number;
        url: string;
        description: string;
        events: string[];
        type: string;
        createdAt: string;
        modifiedAt: string;
      }>;
    }>('/admin/brevo/webhooks');
  }

  async createBrevoWebhook(url: string, events?: string[], description?: string) {
    return this.request<{ id: number; url: string; events: string[] }>('/admin/brevo/webhooks', {
      method: 'POST',
      body: JSON.stringify({ url, events, description }),
    });
  }

  async deleteBrevoWebhook(id: number) {
    return this.request<{ success: boolean }>(`/admin/brevo/webhooks/${id}`, {
      method: 'DELETE',
    });
  }

  // Events Explorer
  async getEventsExplorer(filters: Record<string, any> = {}) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== '' && value !== 'all') {
        params.set(key, String(value));
      }
    }
    return this.request<{
      events: Array<{
        email: string;
        event: string;
        date: string;
        subject: string;
        sender: string;
        project_id: string;
        message_id: string;
      }>;
      total: number;
      page: number;
      pages: number;
    }>(`/admin/brevo/events-explorer?${params}`);
  }

  async getEventsExplorerStats(filters: Record<string, any> = {}) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== '' && value !== 'all') {
        params.set(key, String(value));
      }
    }
    return this.request<{
      total: number;
      sent: number;
      delivered: number;
      opened: number;
      proxy_opened: number;
      clicked: number;
      hard_bounced: number;
      soft_bounced: number;
      unsubscribed: number;
      complained: number;
      blocked: number;
      deferred: number;
      first_event: string | null;
      last_event: string | null;
    }>(`/admin/brevo/events-explorer/stats?${params}`);
  }

  async getEventsExplorerMeta() {
    return this.request<{
      projects: Array<{ id: string; name: string }>;
      eventTypes: string[];
      senders: string[];
    }>('/admin/brevo/events-explorer/meta');
  }

  // Blocklist
  async getBrevoProviders() {
    return this.request<{
      providers: Array<{ id: string; name: string; is_active: number }>;
    }>('/admin/brevo/providers');
  }

  async getBlocklist(providerId: string, params: { limit?: number; offset?: number; startDate?: string; endDate?: string; source?: string } = {}) {
    const qs = new URLSearchParams();
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.offset) qs.set('offset', String(params.offset));
    if (params.startDate) qs.set('startDate', params.startDate);
    if (params.endDate) qs.set('endDate', params.endDate);
    if (params.source) qs.set('source', params.source);
    return this.request<{
      contacts: Array<{
        email: string;
        senderEmail: string;
        reason: { code: string; message: string };
        blockedAt: string;
        marketingBlocked?: boolean;
        transactionalBlocked?: boolean;
      }>;
      total: number;
      limit: number;
      offset: number;
    }>(`/admin/brevo/blocklist/${providerId}?${qs}`);
  }

  async addToBlocklist(providerId: string, emails: string[]) {
    return this.request<{
      success: boolean;
      added: number;
      invalidEmails: number;
      message: string;
    }>(`/admin/brevo/blocklist/${providerId}`, {
      method: 'POST',
      body: JSON.stringify({ emails }),
    });
  }

  async removeFromBlocklist(providerId: string, email: string) {
    return this.request<{ success: boolean; message: string }>(
      `/admin/brevo/blocklist/${providerId}/${encodeURIComponent(email)}`,
      { method: 'DELETE' }
    );
  }

  async backfillBlocklist(providerId: string) {
    return this.request<{ success: boolean; synced: number }>(
      `/admin/brevo/blocklist-backfill/${providerId}`,
      { method: 'POST' }
    );
  }
}

export const api = new ApiClient();
